import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";

import { Button } from "components/ui/Button";
import { usePatientDuePayments } from "features/consultations/hooks/usePatientDuePayments";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { pathGenerators } from "utils/routes";

import s from "./PaymentsSummary.module.css";

// Matches the formatter already duplicated per-page in
// PatientPaymentsPage/constants.jsx and global/PaymentsPage/constants.jsx —
// no shared utils/formatCurrency exists yet, so this follows the same
// per-page-owned pattern rather than introducing a cross-page import.
const formatCurrency = (amount) => `€${parseFloat(amount || 0).toFixed(2)}`;

// Outstanding balance for the Details tab. Sits right under the patient info
// card — the first thing anyone lands on — rather than buried at the bottom
// of the page, since money owed is something front-desk staff need to catch
// before the patient leaves, not something to stumble on after scrolling.
// Only renders when there's actually a balance — a clean bill or a still-
// loading fetch has nothing worth interrupting the page for.
// Reuses the existing (previously unused) usePatientDuePayments hook rather
// than a new backend aggregate endpoint — sums unpaid amountDue client-side.
const PaymentsSummary = () => {
  const navigate = useNavigate();
  const patientId = useCurrentPatientId();
  const { data: duePayments, isLoading } = usePatientDuePayments();

  if (isLoading) return null;

  const owedItems = (duePayments ?? []).filter(
    (payment) => payment.paymentStatus === "DUE"
  );
  const amountOwed = owedItems.reduce(
    (total, payment) => total + parseFloat(payment.amountDue || 0),
    0
  );

  if (amountOwed <= 0) return null;

  const goToPayments = () =>
    navigate(pathGenerators.patient.payments(patientId));

  return (
    <div className={`${s.wrapper} ${s.due}`}>
      <div className={s.iconWrap}>
        <AlertCircle size={20} />
      </div>
      <div className={s.content}>
        <p className={s.amount}>{formatCurrency(amountOwed)} due</p>
        <p className={s.description}>
          {`Owed across ${owedItems.length} unpaid ${
            owedItems.length === 1 ? "item" : "items"
          }`}
        </p>
      </div>
      <Button variant="primary" size="small" onClick={goToPayments}>
        View Payments
      </Button>
    </div>
  );
};

export { PaymentsSummary };
